import {
  CalculatorCategoryBreakdown,
  CalculatorSnapshot,
  hasCategoryBreakdown,
  normalizeCalculatorSnapshot
} from './calculatorSnapshot';

const CATEGORY_META: Array<{ key: keyof CalculatorCategoryBreakdown; label: string; color: string }> = [
  { key: 'transport', label: 'Transportation', color: '#4E79A7' },
  { key: 'housing', label: 'Housing', color: '#F28E2B' },
  { key: 'food', label: 'Food', color: '#59A14F' },
  { key: 'goods', label: 'Goods', color: '#E15759' },
  { key: 'services', label: 'Services', color: '#76B7B2' }
];

const CHART_WIDTH = 640;
const PADDING = { top: 14, right: 16, bottom: 14, left: 16 };
const LABEL_WIDTH = 118;
const VALUE_WIDTH = 52;
const ROW_GAP = 8;
const BAR_HEIGHT = 18;
const TITLE_HEIGHT = 34;

interface ChartLayout {
  barX: number;
  barWidth: number;
  valueRight: number;
}

const getChartLayout = (width: number): ChartLayout => {
  const barX = PADDING.left + LABEL_WIDTH + 10;
  const valueRight = width - PADDING.right;
  const barWidth = valueRight - VALUE_WIDTH - 10 - barX;
  return { barX, barWidth, valueRight };
};

const formatMt = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0.0';
  }
  return value.toFixed(1);
};

const drawValue = (ctx: CanvasRenderingContext2D, text: string, layout: ChartLayout, y: number) => {
  ctx.font = '12px Arial';
  ctx.fillStyle = '#333333';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, layout.valueRight, y);
  ctx.textAlign = 'left';
};

const drawLabel = (ctx: CanvasRenderingContext2D, label: string, y: number) => {
  ctx.font = '13px Arial';
  ctx.fillStyle = '#555555';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, PADDING.left + LABEL_WIDTH, y);
  ctx.textAlign = 'left';
};

const drawBar = (
  ctx: CanvasRenderingContext2D,
  layout: ChartLayout,
  y: number,
  value: number,
  scaleMax: number,
  color: string
) => {
  const trackY = y - BAR_HEIGHT / 2;
  ctx.fillStyle = '#e8e8e8';
  ctx.fillRect(layout.barX, trackY, layout.barWidth, BAR_HEIGHT);

  if (scaleMax > 0 && value > 0) {
    const fillWidth = Math.max(2, (value / scaleMax) * layout.barWidth);
    ctx.fillStyle = color;
    ctx.fillRect(layout.barX, trackY, Math.min(fillWidth, layout.barWidth), BAR_HEIGHT);
  }
};

const drawStackedBarChart = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  categories: CalculatorCategoryBreakdown,
  scaleMax: number
) => {
  const layout = getChartLayout(width);
  const activeCategories = CATEGORY_META.filter(({ key }) => categories[key] > 0);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = '#333333';
  ctx.font = 'bold 15px Arial';
  ctx.textBaseline = 'top';
  const totalLabel = Number.isFinite(scaleMax) && scaleMax > 0
    ? `My carbon footprint: ${formatMt(scaleMax)} mt CO₂e`
    : 'Carbon footprint breakdown (mt CO₂e)';
  ctx.fillText(totalLabel, PADDING.left, PADDING.top);

  const startY = PADDING.top + TITLE_HEIGHT;
  const rowStep = BAR_HEIGHT + ROW_GAP;

  activeCategories.forEach(({ key, label, color }, index) => {
    const value = categories[key];
    const y = startY + index * rowStep + BAR_HEIGHT / 2;
    drawLabel(ctx, label, y);
    drawBar(ctx, layout, y, value, scaleMax, color);
    drawValue(ctx, formatMt(value), layout, y);
  });
};

export const renderFootprintChartDataUrl = (snapshot: CalculatorSnapshot): string => {
  const normalized = normalizeCalculatorSnapshot(snapshot);
  if (!hasCategoryBreakdown(normalized.categories)) {
    return '';
  }
  const activeCategories = CATEGORY_META.filter(({ key }) => normalized.categories[key] > 0);
  const activeValues: number[] = [];
  for (let i = 0; i < activeCategories.length; i++) {
    activeValues.push(normalized.categories[activeCategories[i].key]);
  }
  let categorySum = 0;
  for (let i = 0; i < activeValues.length; i++) {
    categorySum += activeValues[i];
  }
  // Prefer the live headline total; fall back to category sum so the chart never
  // advertises a total that disagrees with the bars.
  const footprintTotal = normalized.footprintMt > 0 ? normalized.footprintMt : categorySum;
  const chartHeight = PADDING.top + PADDING.bottom + TITLE_HEIGHT
    + activeCategories.length * (BAR_HEIGHT + ROW_GAP);

  const canvas = document.createElement('canvas');
  canvas.width = CHART_WIDTH;
  canvas.height = chartHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }
  drawStackedBarChart(
    ctx,
    canvas.width,
    canvas.height,
    scaleCategoriesIfNeeded(normalized.categories, footprintTotal),
    footprintTotal
  );
  return canvas.toDataURL('image/png');
};

const scaleCategoriesIfNeeded = (
  categories: CalculatorCategoryBreakdown,
  targetTotal: number
): CalculatorCategoryBreakdown => {
  if (!(targetTotal > 0)) {
    return categories;
  }
  let sum = 0;
  for (let i = 0; i < CATEGORY_META.length; i++) {
    sum += categories[CATEGORY_META[i].key] || 0;
  }
  if (!(sum > 0) || Math.abs(sum - targetTotal) < 0.05) {
    return categories;
  }
  const scale = targetTotal / sum;
  return {
    transport: categories.transport * scale,
    housing: categories.housing * scale,
    food: categories.food * scale,
    goods: categories.goods * scale,
    services: categories.services * scale
  };
};

export const tryCaptureIframeChart = async (containerId: string): Promise<string | null> => {
  const container = document.getElementById(containerId);
  const iframe = container && container.querySelector('iframe');
  if (!iframe) {
    return null;
  }
  try {
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument || (win && win.document);
    if (!doc) {
      return null;
    }
    const chartRoot = doc.querySelector('#overview_bar_chart, .footprint-chart, [class*="chart"], [class*="Chart"]');
    if (!chartRoot || !(chartRoot instanceof HTMLElement)) {
      return null;
    }
    const chartCanvas = chartRoot.querySelector('canvas');
    if (!(chartCanvas instanceof HTMLCanvasElement)) {
      return null;
    }
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = chartCanvas.width || CHART_WIDTH;
    exportCanvas.height = chartCanvas.height || chartCanvas.clientHeight || 240;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(chartCanvas, 0, 0);
    return exportCanvas.toDataURL('image/png');
  } catch (err) {
    return null;
  }
};

export const captureFootprintChartDataUrl = async (
  snapshot: CalculatorSnapshot,
  containerId: string
): Promise<string> => {
  const normalized = normalizeCalculatorSnapshot(snapshot);
  if (hasCategoryBreakdown(normalized.categories)) {
    return renderFootprintChartDataUrl(normalized);
  }
  const iframeCapture = await tryCaptureIframeChart(containerId);
  if (iframeCapture) {
    return iframeCapture;
  }
  return renderFootprintChartDataUrl(normalized);
};
